// GameClient: WebSocket connection to the GameRoom DO. Server-authoritative.
// Mirrors live state and exposes a store-like surface to the UI.

import { getToken, getIdentity, WS_BASE, type Identity } from "./api";
import { isBlocked } from "./geo";
import { STATIONS, STATION_BY_ID } from "./stations";
import { type ActiveCurse, hasActiveCurse, type GameSize } from "./curses";

// Human-readable name for a pod's hide origin: round 1 = the GM's start-point
// label; later rounds = the station the previous pod was caught at (resolved by
// id, since only the stationId is stored). It's a STARTING reference, not live.
export function podOriginName(origin: { label?: string; stationId?: string } | null | undefined): string {
  if (!origin) return "the game start point";
  return origin.label || (origin.stationId ? (STATION_BY_ID[origin.stationId]?.name ?? "the previous pod's station") : "the game start point");
}

export interface Pin { id: string; lng: number; lat: number; label: string }
export interface Note { id: string; lng: number; lat: number; text: string }
export interface LogItem { id: string; kind: string; text: string; ts: number; count: number; by?: string }
export interface Board {
  stations: Record<string, "eliminated">;
  log: LogItem[];
  pins: Pin[];
  notes: Note[];
  canUndo: boolean;
}
export interface OffLimit { id: string; name: string; geometry: GeoJSON.Geometry }
export interface Run { startedAt: number | null; stoppedAt: number | null; pausedMs: number; pauseStartedAt: number | null }
// Structured question payload — carries everything needed to (a) show the hider
// the right answer chips and (b) let the seeker recompute the elimination from
// the thread alone (stateless across remounts) once the hider answers.
export type QType = "matching" | "measuring" | "thermometer" | "radar" | "photo";
export interface Question {
  id: string; type: QType; catId?: string;
  ref?: [number, number];          // seeker reference point (matching/measuring/radar)
  dist?: number;                    // radar distance / measuring: seeker's nearest distance
  a?: [number, number]; b?: [number, number]; // thermometer endpoints
  place?: string;                   // radar: human label for the reference (e.g. an address/corner)
  photoType?: string;               // photo: the requested photo-type id (see photoTypes.ts)
  reaskDouble?: boolean;            // re-ask of a vetoed slot → hider draws DOUBLE
}
// A shared "what we know about the hider" fact (squad-only). `photoKey` is set on
// hider-sent photos (the R2 object key, loaded via /photo?k=).
export interface Clue { id: string; type: string; text: string; by: string; ts: number; source?: string; photoKey?: string }
// A seeking team/squad a hider can send a photo to.
export interface PhotoTarget { key: string; label: string; members: string[] }
// Hider deck/hand state (mirrors the worker). Hands are keyed by opponent (seeker squad/team) key.
export interface HandCard { instId: string; cardId: string }
export interface Hand { cards: HandCard[]; extra: number }
export interface PendingDraw { drawId: string; opts: HandCard[]; keep: number; reason: string }
export interface DeckCfg { handSize: number; maxTimeCards: number; includeMove: boolean; counts: Record<string, number>; share: "all" | "each" | "groups"; groups: string[][] }
// Round-recap payload from the worker (team leaders' canonical boards).
export interface RecapMomentWire { kind: string; text: string; runTimeSec: number; count?: number; ids?: string[]; photoKey?: string }
export interface RecapWire {
  round: number; roundLabel: string; origin?: string; zoneMode?: string; hidingStationId?: string | null;
  hiders: string[]; outcome?: "caught" | "escaped"; winner?: { squadName: string; members: string[] } | null;
  squadEnds?: { name: string; lat: number; lng: number; caught?: boolean }[];
  squads: { key: string; name: string; members: string[]; steps: { ids: string[]; text: string; gps?: { lat: number; lng: number } }[]; trail?: { lat: number; lng: number; t?: number }[]; elimTimeline?: { ids: string[]; t: number }[]; moments: RecapMomentWire[] }[];
}
export interface Msg {
  id: string; fromTeamId: string; fromName: string; text: string; category?: string; ts: number; fromHider?: boolean;
  question?: Question;              // present on a seeker's structured question
  answer?: string;                  // present on a hider reply: the chosen value
  replyTo?: string;                 // present on a hider reply/photo: the question id answered
  photoKey?: string;                // present on a hider photo message (R2 key → /photo?k=)
  vetoed?: boolean;                 // the hider burned a veto card on this question (no answer, no draw)
  deleted?: boolean;                // the seeker withdrew the question (kept struck-through)
  deletedBy?: string;
  randomized?: boolean;             // the hider played Randomize on it (resolved, answered randomly)
  closed?: boolean;                 // hider manually marked it answered (free-text/verbal reply) → unblocks the seeker
}
export interface Teammate { id: string; name: string; lng: number; lat: number }
export interface Score { cardBonusMin: number; seekingPoints: number }

/** Final hide score in minutes: (run + card bonus) × (1 + points × pct%). */
export function finalMinutes(runMs: number, score: Score | undefined, bonusPct: number, applyPoints: boolean): number {
  const base = runMs / 60000 + (score?.cardBonusMin ?? 0);
  const mult = applyPoints ? 1 + (score?.seekingPoints ?? 0) * (bonusPct / 100) : 1;
  return base * mult;
}
export interface ArchivedRound {
  id: string; endedAt: number; mode?: "seekingteams" | "group";
  hiderTeamId: string | null; hiderSet?: string[]; headStartSec: number; bonusPct?: number;
  offLimits: OffLimit[]; run: Run | null; score?: Score;
  boards: Record<string, { stations: Record<string, "eliminated">; log: LogItem[] }>;
  threads: Record<string, Msg[]>;
}

const emptyBoard = (): Board => ({ stations: {}, log: [], pins: [], notes: [], canUndo: false });
let counter = 0;
export function cryptoId(): string {
  return `${Date.now().toString(36)}${(counter++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}
type Listener = () => void;

/** Format a run clock: head-start counts DOWN to 0 (shown without a minus, the
 * phase label says "head start"), then the hider's run counts up. */
export function runDisplay(run: Run | null, headStartSec: number): { text: string; phase: "idle" | "headstart" | "running" | "paused" | "stopped"; runMs: number } {
  const hs = headStartSec * 1000;
  if (!run || !run.startedAt) return { text: fmtClock(hs), phase: "idle", runMs: 0 };
  const end = run.stoppedAt ?? run.pauseStartedAt ?? Date.now();
  const elapsed = end - run.startedAt - run.pausedMs;
  const rel = elapsed - hs;
  const phase = run.stoppedAt ? "stopped" : run.pauseStartedAt ? "paused" : rel < 0 ? "headstart" : "running";
  const text = fmtClock(Math.abs(rel));
  return { text, phase, runMs: Math.max(0, rel) };
}
function fmtClock(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = String(m).padStart(2, "0"), ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export interface PodSchedule { pods: string[][]; rounds: string[][][] }
export interface PodClientState {
  schedule: PodSchedule | null;
  roundIdx: number;
  origin: { stationId?: string; lng?: number; lat?: number; label?: string } | null;
  catchPoints: Record<string, number>;
  podHideMs: Record<string, number>;
  pending: { squadKey: string; members: string[]; ts: number } | null;
  swingPod: Record<string, number>;
  swingSquad: Record<string, string>;
  names: Record<string, string>;
  mySquad: string;
  bonusMin: number;
}

export interface TeamLead {
  teamKey: string;
  leaderId: string | null;          // primary (first) leader — kept for back-compat
  leaderIds: string[];              // up to two leaders
  name: string | null;             // team name (set by a leader)
  pushes: { from: string; fromName: string; ts: number }[];
  canRevert: boolean;
  members: { sub: string; name: string }[];
}

export class GameClient {
  identity: Identity;
  /** This seeker's PERSONAL research board. */
  personalBoard: Board = emptyBoard();
  /** The team/squad LEADER's curated canonical board (shared with teammates). */
  canonicalBoard: Board = emptyBoard();
  /** Which board the seeker is currently viewing/editing. */
  activeView: "mine" | "canonical" = "mine";
  /** Set once the user manually picks a view, so we stop auto-defaulting it. */
  private viewLocked = false;
  /** Leadership + pending pushes for this seeker's team (null for hider/gm). */
  team: TeamLead | null = null;
  /** The board the UI reads (status, pins, notes, log) — follows activeView. */
  get board(): Board { return this.activeView === "canonical" ? this.canonicalBoard : this.personalBoard; }
  get isLeader(): boolean { return !!this.team && (this.team.leaderIds ?? []).includes(this.identity.sub); }
  get hasLeader(): boolean { return !!this.team && (this.team.leaderIds ?? []).length > 0; }
  /** Whether the active board accepts edits (personal always; canonical only as leader). */
  get canEditActive(): boolean { return this.activeView === "mine" || this.isLeader; }
  offLimits: OffLimit[] = [];
  config: { headStartSec: number; bonusPct: number; zoneMode: string; matchingAssist: boolean; photoTypes?: string[]; gameSize?: string } = { headStartSec: 2700, bonusPct: 10, zoneMode: "quarter", matchingAssist: true };
  /** Game size (S/M/L) — scales curse time values. GM-set; defaults to Small. */
  get gameSize(): GameSize { return (this.config.gameSize === "M" || this.config.gameSize === "L") ? this.config.gameSize : "S"; }
  zoneOverrides: Record<string, GeoJSON.Geometry> = {};
  zoneOverridesV = 0;
  scoresAll: Record<string, Score> = {};
  hider: { stationId: string | null; confirmed: boolean } | null = null;
  /** GM-only: the current hider's chosen spot (for oversight). */
  hiderSpot: { stationId: string | null; confirmed: boolean } | null = null;
  run: Run | null = null;
  runsAll: Record<string, Run> = {};
  threads: Record<string, Msg[]> = {};
  /** Active/cleared curses keyed by cursed squad/team key. Seekers see only their
   * own key; hiders + GM see all (so the hider can verify pending photo-clears). */
  curses: Record<string, ActiveCurse[]> = {};
  clues: Clue[] = [];               // shared squad clue board ("what we know about the hider")
  photoTargets: PhotoTarget[] = []; // (hider only) teams/squads a photo can be sent to
  /** (hider only) the hider's hand per opponent (seeker squad/team) key. */
  hands: Record<string, Hand> = {};
  /** (hider only) a pending "draw N keep 1" per opponent, awaiting the hider to keep. */
  pendingDraws: Record<string, PendingDraw> = {};
  deckCfg: DeckCfg = { handSize: 5, maxTimeCards: 5, includeMove: true, counts: {}, share: "all", groups: [] };
  locations = new Map<string, Teammate>();
  connected = false;
  /** Current hider team (seekingteams mode). */
  hiderTeamId: string | null = null;
  /** Current hider people (group mode). */
  hiderSet: string[] = [];
  mode: "seekingteams" | "group" | "podrotation" = "seekingteams";
  /** Pod Rotation state (only set when mode === "podrotation"). */
  pod: PodClientState | null = null;
  /** Thread/board key for this player as a seeker (shared in group mode). */
  seekerKey = "";
  /** Live gameplay role, derived by the server. */
  role: "hider" | "seeker" | undefined;
  archives: ArchivedRound[] = [];
  /** Called on every init (initial + after a claim/reset) so the app re-routes. */
  onReinit: (() => void) | null = null;
  onClaimDenied: ((reason: string) => void) | null = null;

  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private retry = 1000;
  readonly gameId: string;

  constructor(gameId: string) {
    this.identity = getIdentity()!;
    this.gameId = gameId;
  }

  subscribe(fn: Listener): () => void { this.listeners.add(fn); fn(); return () => this.listeners.delete(fn); }
  private emit() { for (const fn of this.listeners) fn(); }

  connect() {
    const url = `${WS_BASE}?token=${encodeURIComponent(getToken() || "")}&game=${encodeURIComponent(this.gameId)}`;
    this.ws = new WebSocket(url);
    this.ws.addEventListener("open", () => { this.connected = true; this.retry = 1000; this.emit(); });
    this.ws.addEventListener("message", (ev) => this.onMessage(JSON.parse(ev.data as string)));
    this.ws.addEventListener("close", () => { this.connected = false; this.emit(); setTimeout(() => this.connect(), this.retry); this.retry = Math.min(this.retry * 2, 15000); });
    this.ws.addEventListener("error", () => this.ws?.close());
  }

  private onMessage(m: Record<string, unknown>) {
    switch (m.t) {
      case "init":
        this.offLimits = (m.offLimits as OffLimit[]) ?? [];
        this.config = (m.config as { headStartSec: number; bonusPct: number; zoneMode: string; matchingAssist: boolean; photoTypes?: string[]; gameSize?: string }) ?? this.config;
        this.scoresAll = (m.scoresAll as Record<string, Score>) ?? {};
        this.personalBoard = (m.board as Board) ?? emptyBoard();
        {
          const tb = m.team as (TeamLead & { canonical: Board }) | undefined;
          if (tb) {
            this.team = { teamKey: tb.teamKey, leaderId: tb.leaderId ?? null, leaderIds: tb.leaderIds ?? (tb.leaderId ? [tb.leaderId] : []), name: tb.name ?? null, pushes: tb.pushes ?? [], canRevert: !!tb.canRevert, members: tb.members ?? [] };
            this.canonicalBoard = tb.canonical ?? emptyBoard();
            // A team leader's default view is the shared canonical map (so edits
            // help the team), until they manually switch. Non-leaders stay on theirs.
            if (this.isLeader && !this.viewLocked) this.activeView = "canonical";
          } else {
            this.team = null; this.canonicalBoard = emptyBoard(); this.activeView = "mine";
          }
        }
        this.hider = (m.hider as typeof this.hider) ?? null;
        this.hiderSpot = (m.gmHiderSpot as typeof this.hiderSpot) ?? null;
        this.run = (m.run as Run) ?? null;
        this.runsAll = (m.runsAll as Record<string, Run>) ?? {};
        this.threads = (m.threads as Record<string, Msg[]>) ?? {};
        this.curses = (m.curses as Record<string, ActiveCurse[]>) ?? {};
        this.deckCfg = (m.deckCfg as DeckCfg) ?? this.deckCfg;
        this.hands = (m.hands as Record<string, Hand>) ?? {};
        this.pendingDraws = (m.pendingDraws as Record<string, PendingDraw>) ?? {};
        this.clues = (m.clues as Clue[]) ?? [];
        this.photoTargets = (m.photoTargets as PhotoTarget[]) ?? [];
        this.archives = (m.archives as ArchivedRound[]) ?? [];
        this.zoneOverrides = (m.zoneOverrides as Record<string, GeoJSON.Geometry>) ?? {};
        this.zoneOverridesV++;
        this.hiderTeamId = (m.hiderTeamId as string) ?? null;
        this.hiderSet = (m.hiderSet as string[]) ?? [];
        this.mode = (m.mode as "seekingteams" | "group" | "podrotation") ?? "seekingteams";
        this.pod = (m.pod as PodClientState) ?? null;
        this.seekerKey = (m.seekerKey as string) ?? this.identity.teamId ?? "";
        this.role = m.role as "hider" | "seeker" | undefined;
        this.locations = new Map((m.teammates as Teammate[] ?? []).map((t) => [t.id, t]));
        this.onReinit?.();
        break;
      case "board":
        if (m.scope === "canonical") this.canonicalBoard = m.board as Board;
        else this.personalBoard = m.board as Board;
        break;
      case "teamLead":
        if (this.team && this.team.teamKey === (m.teamKey as string)) {
          this.team.leaderId = (m.leaderId as string) ?? null;
          this.team.leaderIds = (m.leaderIds as string[]) ?? (m.leaderId ? [m.leaderId as string] : []);
          this.team.name = (m.name as string) ?? null;
          this.team.canRevert = !!m.canRevert;
          this.team.members = (m.members as TeamLead["members"]) ?? this.team.members;
          if (!this.isLeader) this.team.pushes = []; // pending pushes are leader-only
        }
        break;
      case "pushes":
        if (this.team && this.team.teamKey === (m.teamKey as string)) this.team.pushes = (m.pushes as TeamLead["pushes"]) ?? [];
        break;
      case "offlimits": this.offLimits = m.offLimits as OffLimit[]; break;
      case "config": this.config = m.config as { headStartSec: number; bonusPct: number; zoneMode: string; matchingAssist: boolean; photoTypes?: string[]; gameSize?: string }; break;
      case "zoneOverrides": this.zoneOverrides = m.zoneOverrides as Record<string, GeoJSON.Geometry>; this.zoneOverridesV++; break;
      case "scores": this.scoresAll = m.scores as Record<string, Score>; break;
      case "podPending": if (this.pod) this.pod.pending = m.pending as PodClientState["pending"]; break;
      case "hider": this.hider = m.hider as typeof this.hider; break;
      case "gmHider": this.hiderSpot = m.spot as typeof this.hiderSpot; break;
      case "run": {
        const teamId = m.teamId as string;
        this.runsAll[teamId] = m.run as Run;
        if (teamId === this.identity.teamId) this.run = m.run as Run;
        break;
      }
      case "msg": this.threads = { ...this.threads, [m.threadTeamId as string]: m.messages as Msg[] }; break;
      case "curses": this.curses = { ...this.curses, [m.targetKey as string]: m.curses as ActiveCurse[] }; break;
      case "hands": this.hands = m.hands as Record<string, Hand>; this.pendingDraws = m.pending as Record<string, PendingDraw>; if (m.cfg) this.deckCfg = m.cfg as DeckCfg; break;
      case "deckCfg": this.deckCfg = m.cfg as DeckCfg; break;
      case "clues": if ((m.teamKey as string) === this.seekerKey) this.clues = (m.clues as Clue[]) ?? []; break;
      case "claimDenied": this.onClaimDenied?.(m.reason as string); return;
      case "loc": this.locations.set(m.id as string, { id: m.id as string, name: m.name as string, lng: m.lng as number, lat: m.lat as number }); break;
      case "locGone": this.locations.delete(m.id as string); break;
      case "recap": { const r = this.recapResolvers.shift(); r?.(m.recap); return; }
    }
    this.emit();
  }

  private send(o: unknown) { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(o)); }
  private boardOp(op: Record<string, unknown>) {
    if (!this.canEditActive) { this.emit(); return; } // viewing the leader's map without being the leader → read-only (snap any local drag back)
    this.send({ t: "board", op: { ...op, target: this.activeView } });
  }

  // ---- store-like surface -------------------------------------------------
  statusOf(id: string): "active" | "eliminated" {
    return this.board.stations[id] || isBlocked(id, this.offLimits) ? "eliminated" : "active";
  }
  isOffLimits(id: string): boolean { return isBlocked(id, this.offLimits); }
  activeCount(): number { return STATIONS.reduce((n, s) => n + (this.statusOf(s.id) === "active" ? 1 : 0), 0); }

  eliminate(ids: string[], text: string) { this.boardOp({ kind: "eliminate", ids, text }); }
  cut(ids: string[], text: string) { this.boardOp({ kind: "cut", ids, text }); }
  toggle(id: string) { if (!this.isOffLimits(id)) this.boardOp({ kind: "toggleStation", id }); }
  reset() { this.boardOp({ kind: "reset" }); }
  undoLast() { this.boardOp({ kind: "undoLast" }); }
  undoAction(id: string) { this.boardOp({ kind: "undoAction", id }); }

  addPin(pin: Pin) { this.boardOp({ kind: "addPin", pin }); }
  movePin(id: string, lng: number, lat: number) { this.boardOp({ kind: "movePin", id, lng, lat }); }
  removePin(id: string) { this.boardOp({ kind: "removePin", id }); }
  addNote(note: Note) { this.boardOp({ kind: "addNote", note }); }
  moveNote(id: string, lng: number, lat: number) { this.boardOp({ kind: "moveNote", id, lng, lat }); }
  editNote(id: string, text: string) { this.boardOp({ kind: "editNote", id, text }); }
  removeNote(id: string) { this.boardOp({ kind: "removeNote", id }); }

  /** Is there a still-open question in this thread? (one open at a time) */
  hasOpenQuestion(threadKey: string): boolean {
    const list = this.threads[threadKey] || [];
    return list.some((m) => {
      const qid = m.question?.id;
      // `closed` = hider manually marked it answered (free-text/verbal reply).
      if (m.fromHider || !qid || m.vetoed || m.deleted || m.randomized || m.closed) return false;
      // A photo question is answered by a photo reply (photoKey), not text — count
      // either, else the seeker stays blocked from asking after a photo answer.
      return !list.some((x) => x.replyTo === qid && (x.answer || x.photoKey));
    });
  }
  /** Hider/GM marks a question answered (free-text/verbal reply) → unblocks the seeker. */
  closeQuestion(threadKey: string, qid: string) { this.send({ t: "closeQuestion", op: { threadTeamId: threadKey, qid } }); }
  postMessage(threadTeamId: string, text: string, category?: string, extra?: { question?: Question; answer?: string; replyTo?: string; photoKey?: string }) { this.send({ t: "msg", op: { threadTeamId, text, category, ...extra } }); }
  /** Delete a seeker's own question from a thread (worker only removes it if it hasn't been answered yet — which also clears it from the hider's queue). */
  deleteMessage(threadTeamId: string, id: string) { this.send({ t: "delMsg", op: { threadTeamId, id } }); }

  // ---- Veto & curse cards (hider <-> seeker) ----
  /** Hider burns a veto card on an unanswered question: no answer, no card draw. */
  vetoQuestion(threadTeamId: string, id: string) { this.send({ t: "veto", op: { threadTeamId, id } }); }
  /** Hider casts a curse on a seeker squad/team (targetKey from photoTargets). */
  sendCurse(targetKey: string, curseId: string) { this.send({ t: "curse", op: { targetKey, curseId } }); }
  /** Cursed seeker submits a clear. mode "photo" → awaits hider verification; else clears now. */
  submitCurseClear(instanceId: string, mode: "photo" | "notify", photoKey?: string) { this.send({ t: "curseSubmit", op: { instanceId, mode, photoKey } }); }
  /** Hider lifts a curse (verifies a pending photo, or clears a hider-judged curse). */
  liftCurse(targetKey: string, instanceId: string) { this.send({ t: "curseLift", op: { targetKey, instanceId } }); }
  // ---- Hider deck / hand ----
  /** The hider's hand vs a given opponent (seeker squad/team) key. */
  hand(oppKey: string): Hand { return this.hands[oppKey] ?? { cards: [], extra: 0 }; }
  handLimit(oppKey: string): number { return this.deckCfg.handSize + this.hand(oppKey).extra; }
  hasVetoCard(oppKey: string): boolean { return this.hand(oppKey).cards.some((c) => c.cardId === "veto"); }
  /** Keep card(s) from a pending draw; the rest are discarded. */
  keepDraw(oppKey: string, instIds: string[]) { this.send({ t: "keepDraw", op: { oppKey, instIds } }); }
  /** Play a powerup card (→ messages the seeker team + burns it). */
  playCard(oppKey: string, instId: string, opts: { targetInstId?: string; discardIds?: string[] } = {}) { this.send({ t: "playCard", op: { oppKey, instId, ...opts } }); }
  discardCard(oppKey: string, instId: string) { this.send({ t: "discardCard", op: { oppKey, instId } }); }
  /** Hider manually draws one card from a squad's deck into their hand. */
  drawCard(oppKey: string) { this.send({ t: "drawCard", op: { oppKey } }); }
  /** GM: update deck configuration (hand size, counts, sharing, etc). */
  setDeckCfg(cfg: Partial<DeckCfg>) { this.send({ t: "deckCfg", op: cfg }); }

  /** This seeker squad's curses (active + cleared history). */
  // Only curses actually targeting THIS squad/team key count — defends against
  // stale entries lingering in this.curses across a role-flip (#37).
  get myCurses(): ActiveCurse[] { return (this.curses[this.seekerKey] ?? []).filter((c) => c.targetKey === this.seekerKey); }
  /** Is this seeker's squad currently under an un-cleared curse? Seeker-only —
   * a hider/GM never shows the seeker CURSED badge even with stale state (#37). */
  get amCursed(): boolean { return this.role === "seeker" && hasActiveCurse(this.myCurses); }
  // Round-recap GIF data (the team leaders' canonical boards). Resolves on the worker's "recap" reply.
  private recapResolvers: ((d: unknown) => void)[] = [];
  requestRecap(round?: number): Promise<RecapWire | null> { return new Promise((res) => { this.recapResolvers.push((d) => res(d as RecapWire | null)); this.send({ t: "getRecap", op: { round } }); }); }
  // Shared clue board — any squad member can add/remove (not leader-gated).
  addClue(type: string, text: string, source: "manual" | "auto" = "manual", photoKey?: string) { this.send({ t: "clue", op: { action: "add", type, text, source, photoKey } }); }
  removeClue(id: string) { this.send({ t: "clue", op: { action: "remove", id } }); }
  runOp(kind: string) { this.send({ t: "run", op: { kind } }); }
  setHeadStart(sec: number) { this.send({ t: "config", op: { headStartSec: sec } }); }
  setBonusPct(pct: number) { this.send({ t: "config", op: { bonusPct: pct } }); }
  setZoneMode(mode: string) { this.send({ t: "config", op: { zoneMode: mode } }); }
  setMatchingAssist(on: boolean) { this.send({ t: "config", op: { matchingAssist: on } }); }
  setPhotoTypes(ids: string[]) { this.send({ t: "config", op: { photoTypes: ids } }); }
  setGameSize(size: GameSize) { this.send({ t: "config", op: { gameSize: size } }); }
  setZoneOverride(key: string, geometry: GeoJSON.Geometry) { this.send({ t: "zoneOverride", op: { op: "set", key, geometry } }); }
  clearZoneOverride(key: string) { this.send({ t: "zoneOverride", op: { op: "clear", key } }); }
  setCardBonus(minutes: number) { this.send({ t: "cardBonus", op: { minutes } }); }
  setScore(key: string, fields: { cardBonusMin?: number; seekingPoints?: number }) { this.send({ t: "setScore", op: { key, ...fields } }); }

  // ---- Team-leader canonical map ----
  /** Local view switch (which board the map + Plan tab show/edit). */
  setView(v: "mine" | "canonical") { this.activeView = v; this.viewLocked = true; this.emit(); }
  claimLeader() { this.send({ t: "claimLeader" }); }
  releaseLeader() { this.send({ t: "releaseLeader" }); }
  handoffLeader(userId: string) { this.send({ t: "handoffLeader", op: { userId } }); }
  makeCoLeader(userId: string) { this.send({ t: "coLeader", op: { userId } }); }
  transferLeader(userId: string) { this.send({ t: "coLeader", op: { userId, transfer: true } }); }
  setTeamName(name: string) { this.send({ t: "setTeamName", op: { name } }); }
  pushToLeader() { this.send({ t: "pushToLeader" }); }
  approvePush(fromUserId: string) { this.send({ t: "approvePush", op: { fromUserId } }); }
  revertCanonical() { this.send({ t: "revertCanonical" }); }
  /** Leader unions their personal-map eliminations into the canonical team map. */
  mergePersonalToCanonical() { this.send({ t: "mergePersonal" }); }

  claimHider() { this.send({ t: "claim" }); }
  /** Report catching the hider WITHOUT claiming the hider role (GM sets next hider). */
  reportCaught() { this.send({ t: "caught" }); }
  setHiderTeam(teamId: string) { this.send({ t: "setHiderTeam", op: { teamId } }); }
  setHiders(ids: string[]) { this.send({ t: "setHiders", op: { ids } }); }
  // ---- Pod Rotation ----
  lockSchedule(schedule: PodSchedule, opts: { startPoint?: object; bonusMin?: number; headStartSec?: number; names?: Record<string, string>; totalRounds?: number; maxSquad?: number; keepTogether?: string[][]; keepApart?: string[][] } = {}) { this.send({ t: "lockSchedule", op: { schedule, ...opts } }); }
  podFound() { this.send({ t: "podFound" }); }
  confirmRound() { this.send({ t: "confirmRound" }); }
  moveSwing(userId: string, opts: { pod?: number | null; squad?: string | null }) { this.send({ t: "moveSwing", op: { userId, ...opts } }); }
  /** Add a player to the locked schedule mid-game (no reshuffle). podIdx = round they hide in, or -1 for seeker-only. */
  addLatePlayer(userId: string, podIdx: number, name?: string) { this.send({ t: "addLatePlayer", op: { userId, podIdx, name } }); }
  setPodOrigin(o: { stationId?: string; lng?: number; lat?: number; label?: string }) { this.send({ t: "podOrigin", op: o }); }
  setPodBonus(min: number) { this.send({ t: "config", op: { podBonusMin: min } }); }
  amHider(): boolean { return this.role === "hider"; }

  sendLoc(lng: number, lat: number) { this.send({ t: "loc", lng, lat }); }
  sendLocOff() { this.send({ t: "locOff" }); }
  setHider(stationId: string | null, confirmed: boolean) { this.send({ t: "hider", stationId, confirmed }); }
  offLimitsOp(op: Record<string, unknown>) { this.send({ t: "offlimits", ...op }); }
}
